import { createSaasWorkspace } from '../features/saas/defaults';
import type { MediaItem, Project, UsageSummary } from './types';

export function formatRelativeTime(iso?: string | null): string {
  if (!iso) return 'Just opened';
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return 'Recently';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  try {
    return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return 'Recently';
  }
}

export function homePosterTone(p: Project): string {
  const text = `${p.name || ''}${p.id || ''}${p.genre || ''}${p.type || ''}`.toLowerCase();
  if (/horror|dark|thriller|noir/.test(text)) return 'tone-noir';
  if (/sci-?fi|cyber|future|ai/.test(text)) return 'tone-steel';
  if (/perfume|beauty|romance|fashion|brand|lipstick|cosmetic|makeup|product ad/.test(text)) return 'tone-amber';
  return 'tone-slate';
}

const isVideoPoster = (url = '', mime = '') => /^video\//i.test(mime) || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
const isImagePoster = (url = '', mime = '') => /^image\//i.test(mime) || /\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(url);

interface PosterCandidate {
  url: string;
  mime: string;
}

function posterCandidates(p: Project): PosterCandidate[] {
  const candidates: PosterCandidate[] = [];
  const push = (url?: unknown, mime?: unknown) => {
    if (typeof url === 'string' && url) candidates.push({ url, mime: typeof mime === 'string' ? mime : '' });
  };

  const saasScreen = (p.saasWorkspace as { sources?: Array<{ id: string; role: string; mime: string }> } | undefined)?.sources?.find(source => source.role === 'screen');
  if (saasScreen) push(`/api/media/${saasScreen.id}`, saasScreen.mime);
  push(p.posterUrl, '');
  push((p.poster as { url?: string } | undefined)?.url, '');

  const takeMime = (take: { mime?: unknown; type?: unknown }) => String(take.mime || '') || (take.type === 'video' ? 'video/mp4' : take.type === 'image' ? 'image/png' : '');
  const mediaUrl = (id?: unknown) => typeof id === 'string' && id ? `/api/media/${encodeURIComponent(id)}` : '';

  for (const shot of p.shots || []) {
    for (const take of shot.takes || []) {
      if (take.approved) push(take.url, takeMime(take));
    }
  }
  const campaign = (p.marketingWorkspace as { campaign?: { deliverables?: Array<{ id: string; kind: string }>; content?: Array<{ deliverableId?: string; assetIds?: string[]; status?: string }> } } | undefined)?.campaign;
  const deliveredFirst = [...(campaign?.content || [])].sort((a, b) => Number(['approved', 'ready'].includes(b.status || '')) - Number(['approved', 'ready'].includes(a.status || '')));
  for (const item of deliveredFirst) {
    const kind = campaign?.deliverables?.find(d => d.id === item.deliverableId)?.kind;
    for (const id of item.assetIds || []) push(mediaUrl(id), kind === 'video' ? 'video/mp4' : kind === 'audio' ? 'audio/mpeg' : 'image/png');
  }
  for (const shot of p.shots || []) {
    const frame = shot as { startFrameUrl?: string; image?: string; imageUrl?: string };
    push(frame.startFrameUrl || frame.image || frame.imageUrl, 'image/png');
  }
  for (const asset of p.assets || []) {
    const item = asset as { url?: string; imageUrl?: string; mediaId?: string };
    push(item.url, '');
    push(item.imageUrl || mediaUrl(item.mediaId), 'image/png');
  }
  for (const item of (p.media || []) as MediaItem[]) {
    push(item.url, item.mime);
  }
  for (const shot of p.shots || []) {
    for (const take of shot.takes || []) {
      if (!take.approved) push(take.url, takeMime(take));
    }
  }
  return candidates.filter(candidate => Boolean(candidate.url));
}

export function projectPoster(p: Project): { url: string; kind: 'image' | 'video' | '' } {
  const candidates = posterCandidates(p);
  const image = candidates.find(item => isImagePoster(item.url, item.mime) && !isVideoPoster(item.url, item.mime));
  if (image) return { url: image.url, kind: 'image' };
  const video = candidates.find(item => isVideoPoster(item.url, item.mime));
  if (video) return { url: video.url, kind: 'video' };
  const apiStill = candidates.find(item => /\/api\/media\//.test(item.url) && !isVideoPoster(item.url, item.mime) && !/^audio\//i.test(item.mime));
  if (apiStill) return { url: apiStill.url, kind: 'image' };
  return { url: '', kind: '' };
}

export function needsSchedule(p: Project): boolean {
  return !p.deliveryDate || p.status === 'needs-schedule';
}

export const emptyUsage = (): UsageSummary => ({
  totalCredits: 0,
  totalINR: '₹0.00',
  totalLLMTokens: 0,
  totalGenerations: 0,
  modelBreakdown: {}
});

export function mergeUsage(local: UsageSummary, server: Partial<UsageSummary>): UsageSummary {
  return {
    totalCredits: Math.max(local.totalCredits || 0, server.totalCredits || 0),
    totalINR: server.totalINR || local.totalINR || '₹0.00',
    totalLLMTokens: Math.max(local.totalLLMTokens || 0, server.totalLLMTokens || 0),
    totalGenerations: Math.max(local.totalGenerations || 0, server.totalGenerations || 0),
    modelBreakdown: Object.keys(server.modelBreakdown || {}).length ? server.modelBreakdown! : local.modelBreakdown || {}
  };
}

/* Token counts run to seven figures; a hero-adjacent stat needs them short. */
export function compactNumber(value: number): string {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

export interface UsageModelRow {
  name: string;
  calls: number;
  credits: number;
  tokens: number;
  /* Share of the period's credits, 0-1. The panel headlines spend, so the bar
     beside each model has to encode spend — it was encoding call count, which
     is a different measure and could rank the models the wrong way round. */
  share: number;
}

export function usageModelRows(usage: UsageSummary): UsageModelRow[] {
  const rows = Object.entries(usage.modelBreakdown || {}).map(([name, value]) => {
    const detail = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
    return {
      name,
      calls: Number(detail ? detail.count : value) || 0,
      credits: Number(detail ? detail.credits : 0) || 0,
      tokens: Number(detail ? detail.tokens : 0) || 0
    };
  });
  const totalCredits = rows.reduce((sum, row) => sum + row.credits, 0);
  const totalCalls = rows.reduce((sum, row) => sum + row.calls, 0);
  return rows
    .sort((a, b) => b.credits - a.credits || b.calls - a.calls)
    .slice(0, 5)
    .map(row => ({
      ...row,
      /* Fall back to share of calls when no cost has been recorded yet, so the
         bar never reads as zero spend across the board. */
      share: totalCredits > 0 ? row.credits / totalCredits : totalCalls > 0 ? row.calls / totalCalls : 0
    }));
}

const uid = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
const now = () => new Date().toISOString();

export interface NewProjectInput {
  name: string;
  type: string;
  format: string;
  runtimeMinutes: number;
  idea: string;
  ownerName?: string;
  fps?: number;
  budgetInr?: number;
  lookPreset?: string;
  /* A screenplay brought to the create dialog. It is not the project's script
     yet: nothing has read it, so it waits as an intake the idea stage runs. */
  script?: string;
  scriptFilename?: string;
  scriptApprovedInr?: number;
  estimatePackage?: 'economy' | 'mid' | 'star';
  estimateSnapshot?: Record<string, unknown>;
}

export const FILM_LOOK_PRESETS = [
  { id: 'cinematic-realism', label: 'Live action', medium: 'live-action cinematic realism', realism: 'photoreal, grounded physics, natural anatomy', palette: 'controlled film palette with motivated practical color contrast', lens: 'cinema lenses, natural depth of field, physically plausible lighting', texture: 'real materials, skin texture, fabric weave, believable weathering', avoid: 'anime lines, cartoon proportions, glossy toy surfaces, text overlays, random logos' },
  { id: 'anime-feature', label: 'Anime', medium: 'anime feature film', realism: 'stylized 2D characters with consistent model-sheet proportions', palette: 'clear color scripting, expressive but controlled saturation', lens: 'anime cinematic framing, parallax backgrounds, clean composition', texture: 'cel shading, clean linework, painterly background detail', avoid: 'photoreal skin, live-action faces, 3D plastic look, unreadable text' },
  { id: 'stylized-3d', label: 'Stylized 3D', medium: 'stylized 3D animation', realism: 'appealing sculpted forms, simplified but consistent anatomy', palette: 'designed color keys with readable silhouettes', lens: 'animated feature camera language, soft global illumination', texture: 'clean stylized materials, controlled surface detail', avoid: 'uncanny photoreal faces, gritty documentary noise, inconsistent proportions' },
  { id: 'graphic-cartoon', label: '2D cartoon', medium: 'graphic cartoon illustration', realism: 'flat expressive shapes, exaggerated readable silhouettes', palette: 'bold limited palette with high readability', lens: 'simple staged composition, clear foreground/background separation', texture: 'flat fills, clean outlines, minimal texture', avoid: 'photoreal rendering, cinematic grain, complex material noise, accidental text' },
  { id: 'claymation', label: 'Clay / stop-motion', medium: 'stop-motion claymation', realism: 'sculpted plasticine characters, armature-aware joints, handmade proportions', palette: 'handmade saturated clay colors', lens: 'tabletop stop-motion framing, practical miniature lighting', texture: 'visible fingerprints, tool marks, matte plasticine skin', avoid: 'photoreal skin, CGI smoothness, live-action pores, slick digital materials' }
] as const;

function visualStyleLockFromLook(lookPreset?: string, at = now()) {
  const preset = FILM_LOOK_PRESETS.find(item => item.id === lookPreset) || FILM_LOOK_PRESETS[0];
  return { ...preset, preset: preset.id, updatedAt: at };
}

function productionBudget(input: NewProjectInput) {
  const limitINR = Math.max(1000, Math.round(Number(input.budgetInr) || 25000));
  return { limitINR, warningPercent: 80, maxTakesPerShot: 8, currency: 'INR' as const };
}

/** Mirrors the project skeleton the studio engine creates for a film project. */
export function buildFilmProject(input: NewProjectInput): Project {
  const at = now();
  const format = String(input.format || '16:9');
  return {
    id: uid('project'),
    name: input.name,
    type: input.type,
    format,
    runtime: Math.round((input.runtimeMinutes || 5) * 60),
    genre: 'Not set',
    tone: 'Not set',
    stage: 'idea',
    idea: input.idea,
    logline: '',
    audience: '',
    status: 'draft',
    beginner: true,
    createdAt: at,
    updatedAt: at,
    schedule: {
      startDate: null,
      deliveryDate: null,
      priority: 'normal',
      owner: input.ownerName || '',
      productionStatus: 'planning'
    },
    story: { premise: '', theme: '', ending: '', beats: [], approved: false, text: '' },
    script: '',
    scriptApproved: false,
    scriptFidelity: 'director-interpretation',
    storyBible: null,
    filmDirection: null,
    visualStyleLock: visualStyleLockFromLook(input.lookPreset, at),
    /* A screenplay that arrived with the project waits here rather than being
       written straight into `script`. Nothing has read it yet: the cast, places,
       props and scenes all come from the intake pass, and a script sitting in the
       field as though it had been imported would claim work nobody has done. The
       idea stage picks this up and runs the analysis. */
    ...(input.script?.trim()
      ? {
          pendingScriptIntake: {
            text: input.script,
            filename: input.scriptFilename || 'Pasted screenplay.txt',
            at,
            ...(input.scriptApprovedInr ? { approvedInr: input.scriptApprovedInr } : {})
          },
          storyPath: 'have-script' as const
        }
      : { storyPath: null }),
    ...(input.estimatePackage ? { generationPlan: { selectedPackage: input.estimatePackage, overrides: {} } } : {}),
    ...(input.estimateSnapshot ? { estimateHistory: [input.estimateSnapshot] } : {}),
    scenes: [],
    assets: [],
    shots: [],
    selectedShot: null,
    canvas: [],
    comments: [],
    approvals: [],
    agentMessages: {},
    customSkills: [],
    camera: { lens: 45, duration: 6, path: 'dolly-in' },
    jobs: [],
    history: [],
    timeline: { fps: Number(input.fps || 24), clips: [], aspect: format },
    deliveries: [],
    generationMode: 'shot',
    manualGeneration: { imageRatio: format, videoRatio: format },
    productionControl: { budget: productionBudget(input) }
  };
}

export function buildVfxProject(input: NewProjectInput): Project {
  const project = buildFilmProject(input);
  return {
    ...project,
    projectKind: 'ai-vfx',
    genre: 'VFX',
    tone: 'Production',
    stage: 'generate',
    runtime: 0,
    vfxScope: input.idea,
    agentMessages: { generate: [] },
    camera: { lens: 35, duration: 5, path: 'locked' },
    generationMode: 'video',
    manualGeneration: { videoModel: 'seedance-2.5', imageRatio: project.format, videoRatio: project.format },
    vfxWorkspace: { step: 'modify', workbench: 'tools', filter: 'all' }
  };
}

export function buildMarketingProject(input: NewProjectInput): Project {
  const project = buildFilmProject(input);
  return {
    ...project,
    projectKind: 'marketing',
    stage: 'onboard',
    agentMessages: { onboard: [] },
    marketingWorkspace: { step: 'onboard', product: { name: input.name }, creativeAssets: { productPhotos: [] } },
    ...(input.budgetInr == null ? { productionControl: { budget: { ...productionBudget(input), source: 'system-default' as const } } } : {})
  };
}

export function buildUpscaleProject(input: NewProjectInput): Project {
  const project = buildFilmProject(input);
  return {
    ...project,
    projectKind: 'upscale',
    type: 'Upscale',
    stage: 'upscale',
    runtime: 0,
    upscaleWorkspace: { clips: [], preset: 'common' }
  };
}

export function projectPath(project: Project): string {
  const link = project.seriesLink as { role?: string; seriesId?: string } | undefined;
  if (link?.role === 'series' && link.seriesId) return `/series/${encodeURIComponent(link.seriesId)}`;
  return `/projects/${encodeURIComponent(project.id)}/${project.projectKind === 'saas' ? 'saas' : project.projectKind === 'ai-vfx' ? 'raw' : project.stage || 'idea'}`;
}

export function buildSaasProject(input: NewProjectInput): Project {
  const project = buildFilmProject(input);
  const workspace = createSaasWorkspace(input.idea, input.name);
  workspace.mode = input.type === 'Product walkthrough' ? 'walkthrough' : input.type === 'SaaS showcase' ? 'showcase' : 'hybrid';
  return { ...project, projectKind: 'saas', stage: 'brief', runtime: 0, saasWorkspace: workspace };
}
